/**
 * Weather File Preview Component
 *
 * Modal showing detailed weather file information, climate data, and design days.
 */

import { useState, useEffect, useRef } from 'react';
import type { WeatherFileMetadata, WeatherPreviewTab, ParsedDesignDay, DesignDayMetadata } from '../../types';
import { ASHRAE_CLIMATE_ZONES, COUNTRY_NAMES } from '../../types';
import { useIsMobile } from '../../../hooks';
import { useDesignDays } from '../../hooks/useDesignDays';
import { useClimateStats } from '../../hooks/useClimateStats';
import { DesignDaySelector, DesignDayPreview } from '../DesignDaySelector';
import { injectDesignDays, checkDesignDayConflicts } from '../../services/designDayInjector';
import { MonthlyChart } from './MonthlyChart';

export interface WeatherFilePreviewProps {
  file: WeatherFileMetadata;
  onClose: () => void;
  onSelect: () => void;
}

export function WeatherFilePreview({ file, onClose, onSelect }: WeatherFilePreviewProps) {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<WeatherPreviewTab>('info');
  const [previewDesignDay, setPreviewDesignDay] = useState<ParsedDesignDay | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Focus trap and keyboard handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Focus modal on open
  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  // Design days hook
  const {
    designDays,
    selectedIds,
    recommendedIds,
    groupedDesignDays,
    isLoading: ddLoading,
    error: ddError,
    loadDesignDays,
    toggleSelection,
    selectRecommended,
    clearSelection,
    getSelectedDesignDays,
  } = useDesignDays(file);

  // Climate stats hook
  const {
    stats: climateStats,
    monthlyData,
    isLoading: climateLoading,
    error: climateError,
    loadClimateStats,
  } = useClimateStats(file);

  // Load design days when tab is selected
  useEffect(() => {
    if (activeTab === 'design-days' && !designDays && !ddLoading && !ddError) {
      loadDesignDays();
    }
  }, [activeTab, designDays, ddLoading, ddError, loadDesignDays]);

  // Load climate stats when tab is selected
  useEffect(() => {
    if (activeTab === 'climate' && !climateStats && !climateLoading && !climateError) {
      loadClimateStats();
    }
  }, [activeTab, climateStats, climateLoading, climateError, loadClimateStats]);

  const climateZoneInfo = file.climateZone.ashrae
    ? ASHRAE_CLIMATE_ZONES[file.climateZone.ashrae]
    : null;

  const countryName =
    COUNTRY_NAMES[file.location.countryCode] ?? file.location.country;

  // Handle viewing design day details
  const handleViewDetails = (dd: DesignDayMetadata | ParsedDesignDay) => {
    // If it's a ParsedDesignDay (has idfObject), show preview directly
    if ('idfObject' in dd) {
      setPreviewDesignDay(dd);
    } else {
      // Find the full parsed version
      const full = designDays?.find((d) => d.id === dd.id);
      if (full) {
        setPreviewDesignDay(full);
      }
    }
  };

  // State for injection status
  const [injectionMessage, setInjectionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const messageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    };
  }, []);

  // Handle adding design days to model
  const handleAddToModel = () => {
    const selected = getSelectedDesignDays();
    if (selected.length === 0) return;

    // Check for conflicts
    const conflicts = checkDesignDayConflicts(selected);
    if (conflicts.length > 0) {
      // For now, skip conflicts. TODO: Add conflict resolution dialog
      console.log('Skipping conflicts:', conflicts);
    }

    // Inject design days
    const result = injectDesignDays(selected, { onConflict: 'skip' });

    // Show result message
    if (result.errors.length > 0) {
      setInjectionMessage({
        type: 'error',
        text: `Added ${result.added} design days with ${result.errors.length} errors`,
      });
    } else if (result.added > 0) {
      setInjectionMessage({
        type: 'success',
        text: `Added ${result.added} design day${result.added > 1 ? 's' : ''} to model${result.skipped > 0 ? ` (${result.skipped} skipped)` : ''}`,
      });
    } else if (result.skipped > 0) {
      setInjectionMessage({
        type: 'success',
        text: `${result.skipped} design day${result.skipped > 1 ? 's' : ''} already exist in model`,
      });
    }

    // Clear message after 3 seconds
    if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = setTimeout(() => setInjectionMessage(null), 3000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        padding: isMobile ? '0' : '1rem',
        paddingBottom: isMobile ? '0' : '1rem',
      }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className={`flex w-full flex-col overflow-hidden bg-background shadow-xl focus:outline-none ${
          isMobile
            ? 'h-full max-h-full'
            : 'max-h-[90vh] max-w-2xl rounded-lg'
        }`}
        style={isMobile ? {
          // Account for safe areas on mobile
          paddingBottom: 'env(safe-area-inset-bottom)',
          marginBottom: '3.5rem', // Bottom nav height
        } : undefined}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <h2 id="preview-title" className="text-xl font-semibold">{file.name}</h2>
            <p className="text-sm text-muted-foreground">
              {file.location.city}, {file.location.region}, {countryName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Close preview"
            type="button"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {(['info', 'climate', 'design-days'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                activeTab === tab
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'info' && 'Information'}
              {tab === 'climate' && 'Climate Data'}
              {tab === 'design-days' && 'Design Days'}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Location */}
              <section>
                <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                  Location
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-sm text-muted-foreground">Coordinates</div>
                    <div>
                      {file.location.latitude.toFixed(4)}° N,{' '}
                      {file.location.longitude.toFixed(4)}° E
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Elevation</div>
                    <div>{file.location.elevation} m above sea level</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Timezone</div>
                    <div>UTC{file.location.timezone >= 0 ? '+' : ''}{file.location.timezone}</div>
                  </div>
                  {file.location.wmoId && (
                    <div>
                      <div className="text-sm text-muted-foreground">WMO Station ID</div>
                      <div>{file.location.wmoId}</div>
                    </div>
                  )}
                </div>
              </section>

              {/* Climate Zone */}
              <section>
                <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                  Climate Classification
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  {file.climateZone.ashrae && (
                    <div>
                      <div className="text-sm text-muted-foreground">ASHRAE 169 Zone</div>
                      <div className="flex items-center gap-2">
                        <span className="rounded bg-blue-100 px-2 py-0.5 font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {file.climateZone.ashrae}
                        </span>
                        {climateZoneInfo && (
                          <span className="text-sm">{climateZoneInfo.name}</span>
                        )}
                      </div>
                      {climateZoneInfo && (
                        <div className="mt-1 text-sm text-muted-foreground">
                          {climateZoneInfo.description}
                        </div>
                      )}
                    </div>
                  )}
                  {file.climateZone.koppen && (
                    <div>
                      <div className="text-sm text-muted-foreground">Koppen-Geiger</div>
                      <div>{file.climateZone.koppen}</div>
                    </div>
                  )}
                </div>
              </section>

              {/* Data Source */}
              <section>
                <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                  Data Source
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-sm text-muted-foreground">Type</div>
                    <div>{file.source.type}</div>
                  </div>
                  {file.source.years && (
                    <div>
                      <div className="text-sm text-muted-foreground">Data Period</div>
                      <div>{file.source.years}</div>
                    </div>
                  )}
                  {file.source.provider && (
                    <div>
                      <div className="text-sm text-muted-foreground">Provider</div>
                      <div>{file.source.provider}</div>
                    </div>
                  )}
                </div>
              </section>

              {/* File Info */}
              <section>
                <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                  File
                </h3>
                <div>
                  <div className="text-sm text-muted-foreground">Filename</div>
                  <div className="break-all font-mono text-sm">{file.filename}</div>
                </div>
              </section>

              {/* Tags */}
              {file.tags && file.tags.length > 0 && (
                <section>
                  <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {file.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-muted px-2 py-0.5 text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {activeTab === 'climate' && (
            <div className="space-y-6">
              {climateLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="mt-4">Loading climate data...</p>
                </div>
              ) : climateError ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-destructive mb-4"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="m15 9-6 6" />
                    <path d="m9 9 6 6" />
                  </svg>
                  <p className="font-medium text-destructive">Failed to load climate data</p>
                  <p className="mt-1 text-sm text-muted-foreground">{climateError}</p>
                  <button
                    onClick={loadClimateStats}
                    className="mt-4 rounded border px-4 py-2 text-sm hover:bg-muted"
                  >
                    Retry
                  </button>
                </div>
              ) : climateStats ? (
                <>
                  {/* Temperature */}
                  <section>
                    <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                      Temperature
                    </h3>
                    <div className="grid gap-4 sm:grid-cols-3">
                      {climateStats.avgTemp !== undefined && (
                        <div className="rounded-lg bg-muted/50 p-3 text-center">
                          <div className="text-2xl font-bold">
                            {climateStats.avgTemp.toFixed(1)}°C
                          </div>
                          <div className="text-sm text-muted-foreground">Average</div>
                        </div>
                      )}
                      {climateStats.minTemp !== undefined && (
                        <div className="rounded-lg bg-blue-50 p-3 text-center dark:bg-blue-900/20">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {climateStats.minTemp.toFixed(1)}°C
                          </div>
                          <div className="text-sm text-muted-foreground">Minimum</div>
                        </div>
                      )}
                      {climateStats.maxTemp !== undefined && (
                        <div className="rounded-lg bg-red-50 p-3 text-center dark:bg-red-900/20">
                          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {climateStats.maxTemp.toFixed(1)}°C
                          </div>
                          <div className="text-sm text-muted-foreground">Maximum</div>
                        </div>
                      )}
                    </div>
                    {monthlyData && (
                      <div className="mt-4 rounded-lg border p-3">
                        <MonthlyChart
                          data={monthlyData}
                          series={[
                            { key: 'maxTemp', color: '#ef4444', label: 'Max' },
                            { key: 'avgTemp', color: '#6b7280', label: 'Avg' },
                            { key: 'minTemp', color: '#3b82f6', label: 'Min' },
                          ]}
                          unit="°"
                        />
                      </div>
                    )}
                  </section>

                  {/* Degree Days */}
                  {(climateStats.hdd18 !== undefined || climateStats.cdd10 !== undefined) && (
                    <section>
                      <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                        Degree Days (Annual)
                      </h3>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {climateStats.hdd18 !== undefined && (
                          <div className="rounded-lg border p-3">
                            <div className="text-xl font-bold">
                              {Math.round(climateStats.hdd18).toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Heating Degree Days (base 18°C)
                            </div>
                          </div>
                        )}
                        {climateStats.cdd10 !== undefined && (
                          <div className="rounded-lg border p-3">
                            <div className="text-xl font-bold">
                              {Math.round(climateStats.cdd10).toLocaleString()}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Cooling Degree Days (base 10°C)
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  )}

                  {/* Solar Radiation */}
                  {(climateStats.avgGHI !== undefined || climateStats.avgDNI !== undefined) && (
                    <section>
                      <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                        Solar Radiation
                      </h3>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {climateStats.avgGHI !== undefined && (
                          <div className="rounded-lg border p-3">
                            <div className="text-xl font-bold">
                              {Math.round(climateStats.avgGHI).toLocaleString()} Wh/m²
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Avg Global Horizontal Irradiance
                            </div>
                          </div>
                        )}
                        {climateStats.avgDNI !== undefined && (
                          <div className="rounded-lg border p-3">
                            <div className="text-xl font-bold">
                              {Math.round(climateStats.avgDNI).toLocaleString()} Wh/m²
                            </div>
                            <div className="text-sm text-muted-foreground">
                              Avg Direct Normal Irradiance
                            </div>
                          </div>
                        )}
                      </div>
                      {monthlyData && (
                        <div className="mt-4 rounded-lg border p-3">
                          <MonthlyChart
                            data={monthlyData}
                            series={[
                              { key: 'avgGHI', color: '#f59e0b', label: 'GHI' },
                              { key: 'avgDNI', color: '#d97706', label: 'DNI' },
                            ]}
                          />
                        </div>
                      )}
                    </section>
                  )}

                  {/* Wind */}
                  {climateStats.avgWindSpeed !== undefined && (
                    <section>
                      <h3 className="mb-3 text-sm font-medium uppercase text-muted-foreground">
                        Wind
                      </h3>
                      <div className="rounded-lg border p-3">
                        <div className="text-xl font-bold">
                          {climateStats.avgWindSpeed.toFixed(1)} m/s
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Average Wind Speed
                        </div>
                      </div>
                    </section>
                  )}
                </>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mx-auto mb-4"
                  >
                    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
                  </svg>
                  <p>Climate statistics not available for this file.</p>
                  <p className="mt-1 text-sm">
                    Select and download the file to analyze its climate data.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'design-days' && groupedDesignDays && (
            <DesignDaySelector
              groupedDesignDays={groupedDesignDays}
              selectedIds={selectedIds}
              recommendedIds={recommendedIds}
              isLoading={ddLoading}
              error={ddError}
              onToggle={toggleSelection}
              onViewDetails={handleViewDetails}
              onSelectRecommended={selectRecommended}
              onClearAll={clearSelection}
              onAddToModel={handleAddToModel}
              canAddToModel={true}
            />
          )}

          {activeTab === 'design-days' && !groupedDesignDays && !ddLoading && !ddError && (
            <div className="py-8 text-center text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mx-auto mb-4"
              >
                <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                <line x1="16" x2="16" y1="2" y2="6" />
                <line x1="8" x2="8" y1="2" y2="6" />
                <line x1="3" x2="21" y1="10" y2="10" />
              </svg>
              <p>Loading design days...</p>
              <p className="mt-1 text-sm">
                Design day data will be fetched from the weather archive.
              </p>
            </div>
          )}

          {activeTab === 'design-days' && ddLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="mt-4">Loading design days...</p>
            </div>
          )}

          {activeTab === 'design-days' && ddError && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-destructive mb-4"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m15 9-6 6" />
                <path d="m9 9 6 6" />
              </svg>
              <p className="font-medium text-destructive">Failed to load design days</p>
              <p className="mt-1 text-sm text-muted-foreground">{ddError}</p>
              <button
                onClick={loadDesignDays}
                className="mt-4 rounded border px-4 py-2 text-sm hover:bg-muted"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Design Day Preview Modal */}
        {previewDesignDay && (
          <DesignDayPreview
            designDay={previewDesignDay}
            onClose={() => setPreviewDesignDay(null)}
          />
        )}

        {/* Injection Message Toast */}
        {injectionMessage && (
          <div
            className={`fixed bottom-20 left-1/2 -translate-x-1/2 rounded-lg px-4 py-2 text-sm font-medium shadow-lg ${
              injectionMessage.type === 'success'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/80 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900/80 dark:text-red-200'
            }`}
          >
            {injectionMessage.text}
          </div>
        )}

        {/* Footer */}
        <div className={`border-t p-4 ${isMobile ? 'space-y-3' : 'flex items-center justify-between'}`}>
          <a
            href={file.urls.epw}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-sm text-primary hover:underline ${isMobile ? 'block text-center' : ''}`}
          >
            Download from source
          </a>
          <div className={`flex gap-2 ${isMobile ? 'flex-col' : ''}`}>
            <button
              onClick={onClose}
              className={`rounded-md border px-4 py-2 text-sm hover:bg-muted ${isMobile ? 'order-2' : ''}`}
            >
              Cancel
            </button>
            <button
              onClick={onSelect}
              className={`rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 ${isMobile ? 'order-1' : ''}`}
            >
              Use This File
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
